import dayjs from 'dayjs';
import {atomWithStorage, createJSONStorage} from 'jotai/utils';

import {atomWithSessionDayjsFollowingToday, atomWithSessionDayjsStorage} from './atomWithSessionDayjsStorage';
import {EMPTY_WELL_SELECTION, type WellSelection} from '@/lib/wellSelection';
import {DEFAULT_STATUS_FILTER, type StatusFilterValue} from '@/components/StatusFilterToggle.tsx';

export const reportDateAtom = atomWithSessionDayjsStorage('reportDate', dayjs());

// Separate from reportDateAtom on purpose: reportDateAtom is a shared
// "as of this specific day" control wired into TopNav/BottomNav for
// daily-granularity pages. The Monthly Report page only cares about
// which month is selected, so it gets its own month-granularity atom —
// changing one doesn't silently affect the other.
//
// As of 2026-09-02 this is the *start* month of the Monthly Report's date
// range — see reportMonthRangeEndAtom below for the end month. Keeping
// the original key/name (rather than renaming to something like
// reportMonthRangeStartAtom) preserves whatever a returning session
// already has stored here.
export const reportMonthAtom = atomWithSessionDayjsStorage('reportMonth', dayjs().startOf('month'));

// Same "own atom, own reasoning" treatment as reportMonthAtom above —
// Ogp03iExportPage's month selection is its own workflow (an admin
// preparing a specific past month's SSI import can easily be looking at
// a different month than whatever the Monthly Report page happens to be
// showing), so it doesn't share reportMonthAtom.
export const ogp03iExportMonthAtom = atomWithSessionDayjsStorage('ogp03iExportMonth', dayjs().startOf('month'));

// End of the Monthly Report's date range — reportMonthAtom above is the
// range's start month. Added 2026-09-02 once the backend landed
// startDate/endDate on report_MonthlyProduction/
// report_WellMonthlyProduction (see docs/monthly-report-date-range-spec.md
// and backend_requests.md item 4), replacing the page's old single-month
// picker. Same default (today's month) as reportMonthAtom, so a fresh
// session still starts on a plain single-month "range" (start === end)
// exactly like the old picker's default, until the user picks a wider
// range via ReportsPage.tsx's MonthRangePicker.
export const reportMonthRangeEndAtom = atomWithSessionDayjsStorage('reportMonthRangeEnd', dayjs().startOf('month'));

// Production Report's own filters (Duane, 2026-09-11: "it would be good
// if the filters for the production report could be persisted for the
// session") — sessionStorage, same convention as reportMonthAtom/
// reportMonthRangeEndAtom above, so a refresh or navigating away and back
// keeps the same well + date range for the rest of this browser tab's
// session, but a genuinely new session (new tab/window) starts fresh
// rather than reopening whatever well was last looked at.
//
// Only the well's id is persisted, not the whole Well object — the real
// Well list only loads asynchronously (a Well_All query), so
// ProductionReportPage.tsx re-derives the selected Well by looking this
// id up in that list once it's loaded, the same way a stale/renamed well
// would self-correct rather than a stored stale object silently
// disagreeing with the real one.
// CHANGED 2026-09-22 from a single `string | null` well id to a full
// WellSelection (lease-aware multi-well picker — client request: the
// Production Report should take a single well, a whole lease, or an
// arbitrary set of wells; see lib/wellSelection.ts and
// components/WellLeaseSelect.tsx for the full design). New storage key
// (not a migration of the old 'productionReportWellId' key) since the
// shape changed entirely — a stale bare string under the old key would
// fail JSON-shape expectations here rather than silently misparsing.
export const productionReportSelectionAtom = atomWithStorage<WellSelection>(
   'productionReportSelection',
   EMPTY_WELL_SELECTION,
   createJSONStorage<WellSelection>(() => sessionStorage),
);
export const productionReportFromDateAtom = atomWithSessionDayjsStorage(
   'productionReportFromDate',
   dayjs().startOf('month').startOf('day'),
);
// Follows today until the user picks an earlier day (see
// atomWithSessionDayjsFollowingToday) — same stale-date fix as runTicketsToDateAtom below.
export const productionReportToDateAtom = atomWithSessionDayjsFollowingToday('productionReportToDate', 'startOf');

// RunTicketsPage's own From/To date-range filter — added 2026-09-18 per
// Duane's decomposition of the shared, unbounded RunTicket_AllWhere
// query (see graphql/custom/RunTicket_AllWhere.graphql and
// RunTicketsPage_AllWhere.graphql's header comments for the full
// rationale). Same sessionStorage convention/reasoning as
// productionReportFromDateAtom/productionReportToDateAtom above — a
// refresh or navigating away and back keeps the same picked range for
// the rest of this browser tab's session, a genuinely new session starts
// fresh. Defaults to "current month to date" per Duane ("Current month
// to date" — the default), mirroring productionReportFromDateAtom's own
// startOf('month').startOf('day') shape; toDate defaults to the end of
// today (not productionReportToDateAtom's startOf('day')) so a fresh
// session's range actually includes any ticket picked up earlier today.
// RunTicketsPage.tsx itself enforces the separate 1-year max span Duane
// also asked for ("Yes — cap it at 1 year") whenever either date changes
// — these two atoms just hold whatever the last valid range was.
export const runTicketsFromDateAtom = atomWithSessionDayjsStorage(
   'runTicketsFromDate',
   dayjs().startOf('month').startOf('day'),
);
// FIXED 2026-10-27: toDate now FOLLOWS TODAY until the user picks an earlier
// day (see atomWithSessionDayjsFollowingToday). The old frozen
// `dayjs().endOf('day')` default stayed on whatever day the tab was first
// opened (sessionStorage survives refresh), greying out all later dates in
// the From picker and hiding newer tickets.
export const runTicketsToDateAtom = atomWithSessionDayjsFollowingToday('runTicketsToDate');

// BarrelTestsPage's own From/To test-date range — added 2026-10-12 (Duane:
// the unbounded BarrelTest_AllWhere fetch exceeded the 25s request
// timeout). Same session-persisted convention as the run ticket atoms
// above. Defaults to the last 90 days.
export const barrelTestsFromDateAtom = atomWithSessionDayjsStorage(
   'barrelTestsFromDate',
   dayjs().subtract(90, 'day').startOf('day'),
);
export const barrelTestsToDateAtom = atomWithSessionDayjsFollowingToday('barrelTestsToDate');

// localStorage (not sessionStorage) so the Active/Inactive/All choice on
// WellsAdminPage/UsersAdminPage persists across browser sessions/tabs,
// the same way cachedSessionAtom does. Two separate atoms/keys — not one
// shared atom — so the two admin tables' filters don't cross-contaminate
// each other.
const statusFilterStorage = createJSONStorage<StatusFilterValue>(() => localStorage);

export const wellsStatusFilterAtom = atomWithStorage<StatusFilterValue>(
   'wellsStatusFilter',
   DEFAULT_STATUS_FILTER,
   statusFilterStorage,
);

export const usersStatusFilterAtom = atomWithStorage<StatusFilterValue>(
   'usersStatusFilter',
   DEFAULT_STATUS_FILTER,
   statusFilterStorage,
);

// WellEditPage's Production & Downtime "Date Range" filter — remembers the
// last BOUNDED choice ('7'/'30'/'90') across visits, shared by every well.
// "All Time" is never written here: it's a slow, ever-growing load, so
// choosing it applies to that visit only (a local flag on the page) and the
// next visit returns to this remembered bounded range. Read it through
// toBoundedRange() (lib/wellProductionRange.ts) — a stale 'all' left in
// localStorage by an earlier build is treated as the default.
export const wellProductionDateRangeAtom = atomWithStorage<string>(
   'wellProductionDateRange',
   '7',
   createJSONStorage<string>(() => localStorage),
);

// ADDED 2026-08-28 (Duane): now that TopNav/BottomNav have a real,
// always-visible "Offline" indicator (see authAtoms.ts's isOnlineAtom and
// the App-Development Plan's offline requirement it satisfies), the
// reassuring "You're offline — this report will save on your device..."
// message on DailyReportPage.tsx doesn't need to repeat itself every
// single time — Duane's call: "If we show they are offline, is this
// message really needed? ... For sure only show it the first time."
// localStorage (not sessionStorage) so "first time" means ever on this
// device, not just this browser tab/session — matches
// wellsStatusFilterAtom/usersStatusFilterAtom's reasoning above.
export const hasSeenOfflineReportMessageAtom = atomWithStorage<boolean>(
   'hasSeenOfflineReportMessage',
   false,
   createJSONStorage<boolean>(() => localStorage),
);
